// gatsby-browser.js
import "./src/components/youtube-background.css"
// ↓ keep your existing import of loadMetaPixelOnce
import { loadMetaPixelOnce } from "./src/utils/pixel";
import { hasAnalyticsConsent } from "./src/utils/consent";
import {
  getContactEventName,
  getContactEventParameters,
} from "./src/utils/contact-events";
const PIXEL_ID = "7117590368294752";

const runIdle = (fn) =>
  (typeof window !== "undefined" && "requestIdleCallback" in window)
    ? window.requestIdleCallback(fn)
    : setTimeout(fn, 0);

const ANALYTICS_ID = "G-EEP9599659";
const ADS_ID = "AW-11533807327";
const SCRIPT_DELAY_MS = 8000;

let googleTagLoaded = false;
let googleTagBootstrapped = false;
let analyticsConfigured = false;
let adsConfigured = false;
let analyticsLoadQueued = false;
let metaLoadQueued = false;
let webVitalsRegistered = false;
const initialPathname =
  typeof window !== "undefined" ? window.location.pathname : "";

function preconnect(url) {
  if (document.head.querySelector(`link[rel="preconnect"][href="${url}"]`)) {
    return;
  }
  const l = document.createElement("link");
  l.rel = "preconnect";
  l.href = url;
  l.crossOrigin = "anonymous";
  document.head.appendChild(l);
}

function runAfterInteractionOrTimeout(fn, timeout = SCRIPT_DELAY_MS) {
  if (typeof window === "undefined") return;
  let done = false;

  const finish = () => {
    if (done) return;
    done = true;
    window.removeEventListener("pointerdown", finish);
    window.removeEventListener("keydown", finish);
    window.removeEventListener("touchstart", finish);
    window.removeEventListener("scroll", finish);
    clearTimeout(timer);
    runIdle(fn);
  };

  const timer = window.setTimeout(finish, timeout);
  window.addEventListener("pointerdown", finish, { once: true, passive: true });
  window.addEventListener("keydown", finish, { once: true });
  window.addEventListener("touchstart", finish, { once: true, passive: true });
  window.addEventListener("scroll", finish, { once: true, passive: true });
}

function ensureGoogleTagBootstrap() {
  if (googleTagBootstrapped) return;
  googleTagBootstrapped = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
}

function loadGoogleTagLibrary() {
  if (googleTagLoaded) return;
  googleTagLoaded = true;
  preconnect("https://www.googletagmanager.com");
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ANALYTICS_ID)}`;
  document.head.appendChild(s);
}

function configureAnalytics() {
  if (analyticsConfigured || !window.gtag) return;
  analyticsConfigured = true;
  window.gtag("config", ANALYTICS_ID, {
    anonymize_ip: true,
    cookie_expires: 0,
    send_page_view: false,
  });
}

function configureAds() {
  if (adsConfigured || !window.gtag) return;
  adsConfigured = true;
  window.gtag("config", ADS_ID, {
    anonymize_ip: true,
    cookie_expires: 0,
    send_page_view: false,
  });
}

function queueGoogleTagLoad() {
  if (analyticsLoadQueued) return;
  analyticsLoadQueued = true;
  runAfterInteractionOrTimeout(() => {
    if (!window.__analyticsConsentGranted && !window.__adsConsentGranted) return;
    loadGoogleTagLibrary();
  });
}

function queueMetaPixelLoad() {
  if (metaLoadQueued) return;
  metaLoadQueued = true;
  runAfterInteractionOrTimeout(() => {
    if (!window.__adsConsentGranted) return;
    preconnect("https://connect.facebook.net");
    loadMetaPixelOnce(PIXEL_ID);
    if (window.fbq) {
      window.fbq("consent", "grant");
      window.fbq("track", "PageView");
    }
  });
}

function registerWebVitals() {
  if (webVitalsRegistered) return;
  webVitalsRegistered = true;

  // This RUM stream only represents visitors who grant analytics consent.
  // Treat it as an immediate diagnostic signal, not an unbiased field p75.
  runIdle(() => {
    import("web-vitals/attribution").then(({ onLCP, onCLS, onINP }) => {
      function sendVital(name, metric) {
        if (!window.__analyticsConsentGranted || !window.gtag) return;

        window.gtag("event", name, {
          value: Math.round(
            name === "CLS" ? metric.value * 1000 : metric.value,
          ),
          event_label: metric.id,
          metric_rating: metric.rating,
          navigation_type: metric.navigationType,
          ...metric.attribution,
          page_location: window.location.href,
          page_path: initialPathname,
          non_interaction: true,
        });
      }

      onLCP((metric) => sendVital("LCP", metric));
      onCLS((metric) => sendVital("CLS", metric));
      onINP((metric) => sendVital("INP", metric));
    });
  });
}

// send a GA4 page_view (no-op until GA is loaded & consent granted)
function sendPageView(loc) {
  if (window.__analyticsConsentGranted && window.gtag) {
    window.gtag("event", "page_view", {
      page_location: window.location.href,
      page_path: loc ? loc.pathname + (loc.search || "") : window.location.pathname + window.location.search,
      page_title: document.title,
    });
    window.__firstPVSent = true;
  }
}

// Tiny helper
function safeGtag() { if (window.gtag) window.gtag.apply(window, arguments); }

function registerContactLinkTracking() {
  if (window.__ga4ContactLinkTrackingRegistered) return;
  window.__ga4ContactLinkTrackingRegistered = true;

  document.addEventListener("click", (clickEvent) => {
    const target = clickEvent.target;
    const link =
      target && typeof target.closest === "function"
        ? target.closest("a[href]")
        : null;

    if (!link || !hasAnalyticsConsent() || !window.gtag) return;

    const eventName = getContactEventName(
      link.getAttribute("href"),
      window.location.origin,
    );
    if (!eventName) return;

    const isMobile =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 767px)").matches;

    window.gtag(
      "event",
      eventName,
      getContactEventParameters(link, window.location, isMobile),
    );
  });
}

// ---- Consent bridge (Cookiebot → your app) ----
if (typeof window !== "undefined") {
  window.__applyConsent = function ({ analytics, ads }) {
    const prevAnalytics = !!window.__analyticsConsentGranted;
    const prevAds = !!window.__adsConsentGranted;

    window.__analyticsConsentGranted = !!analytics;
    window.__adsConsentGranted = !!ads;

    // Consent Mode v2
    safeGtag("consent", "update", {
      analytics_storage: analytics ? "granted" : "denied",
      ad_storage: ads ? "granted" : "denied",
      ad_user_data: ads ? "granted" : "denied",
      ad_personalization: ads ? "granted" : "denied",
    });

    // → Analytics just granted: load GA, web-vitals and send PV
    if (analytics && !prevAnalytics) {
      ensureGoogleTagBootstrap();
      configureAnalytics();
      queueGoogleTagLoad();

      registerWebVitals();
      sendPageView();
    }

    // → Ads consent: configure Google Ads + Meta only then
    if (ads && !prevAds) {
      ensureGoogleTagBootstrap();
      configureAds();
      queueGoogleTagLoad();
      queueMetaPixelLoad();
    } else if (window.fbq) {
      window.fbq("consent", "revoke");
    }
  };

  // Cookiebot hooks
  window.addEventListener("CookiebotOnConsentReady", () => {
    const c = window.Cookiebot && window.Cookiebot.consent;
    window.__applyConsent({ analytics: !!(c && c.statistics), ads: !!(c && c.marketing) });
  });
  window.addEventListener("CookiebotOnAccept", () => {
    const c = window.Cookiebot && window.Cookiebot.consent;
    window.__applyConsent({ analytics: !!(c && c.statistics), ads: !!(c && c.marketing) });
  });
  window.addEventListener("CookiebotOnDecline", () => {
    window.__applyConsent({ analytics: false, ads: false });
  });
}

export const onClientEntry = () => {
  registerContactLinkTracking();
};

// Fire PVs in CSR only if consented
export const onInitialClientRender = () => { sendPageView(); };
export const onRouteUpdate = ({ location }) => {
  runIdle(() => sendPageView(location));
};
